import { deepEqual as equal } from 'fast-equals'
import { MakeLogicType, actions, afterMount, kea, key, listeners, path, props, reducers } from 'kea'
import { actionToUrl, router, urlToAction } from 'kea-router'
import posthog from 'posthog-js'

import { Params } from 'scenes/sceneTypes'

import { ErrorTrackingIssue, ErrorTrackingQuery } from '~/queries/schema/schema-general'

import type {
    ErrorTrackingIssueAssignee,
    ErrorTrackingOrderBy,
} from '../../../../../frontend/src/queries/schema/schema-general'
import { syncSearchParams, updateSearchParams } from '../../utils'

export type ErrorTrackingQueryOrderBy = ErrorTrackingQuery['orderBy']
export type ErrorTrackingQueryOrderDirection = ErrorTrackingQuery['orderDirection']
export type ErrorTrackingQueryAssignee = ErrorTrackingQuery['assignee']
export type ErrorTrackingQueryStatus = ErrorTrackingQuery['status']

export const ORDER_BY_OPTIONS: Record<ErrorTrackingQueryOrderBy, string> = {
    last_seen: 'Last seen',
    first_seen: 'First seen',
    occurrences: 'Occurrences',
    users: 'Users',
    sessions: 'Sessions',
}
const DEFAULT_ORDER_BY: ErrorTrackingQueryOrderBy = 'last_seen'

// A persisted (localStorage) or tool-provided orderBy can hold a value that's no longer a
// valid sort field — e.g. an option that was renamed or removed. Passing it through to the
// query fails the whole page, so anything unrecognized falls back to the default.
export function isValidOrderBy(orderBy: unknown): orderBy is ErrorTrackingQueryOrderBy {
    return typeof orderBy === 'string' && Object.hasOwn(ORDER_BY_OPTIONS, orderBy)
}

const DEFAULT_ORDER_DIRECTION = 'DESC'
const DEFAULT_ASSIGNEE = null
const DEFAULT_STATUS = 'active'

export interface IssueQueryOptionsLogicProps {
    logicKey: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issueQueryOptionsLogicValues {
    assignee: ErrorTrackingQueryAssignee | null
    orderBy: ErrorTrackingQueryOrderBy
    orderDirection: ErrorTrackingQueryOrderDirection
    status: ErrorTrackingQueryStatus
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issueQueryOptionsLogicActions {
    setAssignee: (assignee: ErrorTrackingIssue['assignee']) => {
        assignee: ErrorTrackingIssueAssignee | null
    }
    setOrderBy: (orderBy: ErrorTrackingQueryOrderBy) => {
        orderBy: ErrorTrackingOrderBy
    }
    setOrderDirection: (orderDirection: ErrorTrackingQueryOrderDirection) => {
        orderDirection: 'ASC' | 'DESC' | undefined
    }
    setStatus: (status: ErrorTrackingQueryStatus) => {
        status: ErrorTrackingQueryStatus | undefined
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issueQueryOptionsLogicMeta {
    key: string
}

export type issueQueryOptionsLogicType = MakeLogicType<
    issueQueryOptionsLogicValues,
    issueQueryOptionsLogicActions,
    IssueQueryOptionsLogicProps,
    issueQueryOptionsLogicMeta
>

export const issueQueryOptionsLogic = kea<issueQueryOptionsLogicType>([
    path(['products', 'error_tracking', 'components', 'IssueQueryOptions', 'issueQueryOptionsLogic']),
    props({} as IssueQueryOptionsLogicProps),
    key(({ logicKey }) => logicKey),

    actions({
        setOrderBy: (orderBy: ErrorTrackingQueryOrderBy) => ({ orderBy }),
        setOrderDirection: (orderDirection: ErrorTrackingQueryOrderDirection) => ({ orderDirection }),
        setAssignee: (assignee: ErrorTrackingIssue['assignee']) => ({ assignee }),
        setStatus: (status: ErrorTrackingQueryStatus) => ({ status }),
    }),

    reducers({
        orderBy: [
            DEFAULT_ORDER_BY as ErrorTrackingQueryOrderBy,
            { persist: true },
            {
                setOrderBy: (_, { orderBy }) => (isValidOrderBy(orderBy) ? orderBy : DEFAULT_ORDER_BY),
            },
        ],
        orderDirection: [
            DEFAULT_ORDER_DIRECTION as ErrorTrackingQueryOrderDirection,
            { persist: true },
            {
                setOrderDirection: (_, { orderDirection }) => orderDirection,
            },
        ],
        assignee: [
            DEFAULT_ASSIGNEE as ErrorTrackingQueryAssignee | null,
            { persist: true },
            {
                setAssignee: (_, { assignee }) => assignee,
            },
        ],
        status: [
            DEFAULT_STATUS as ErrorTrackingQueryStatus,
            { persist: true },
            {
                setStatus: (_, { status }) => status,
            },
        ],
    }),

    listeners(({ values }) => ({
        setOrderBy: ({ orderBy }) => {
            posthog.capture('error_tracking_issues_sorted', {
                sort_by: orderBy,
                sort_direction: values.orderDirection,
            })
        },
        setOrderDirection: ({ orderDirection }) => {
            posthog.capture('error_tracking_issues_sorted', {
                sort_by: values.orderBy,
                sort_direction: orderDirection,
            })
        },
    })),

    actionToUrl(({ values }) => {
        const buildURL = (): [
            string,
            Params,
            Record<string, any>,
            {
                replace: boolean
            },
        ] => {
            return syncSearchParams(router, (params: Params) => {
                updateSearchParams(params, 'assignee', values.assignee, DEFAULT_ASSIGNEE)
                updateSearchParams(params, 'status', values.status, DEFAULT_STATUS)
                updateSearchParams(params, 'orderBy', values.orderBy, DEFAULT_ORDER_BY)
                updateSearchParams(params, 'orderDirection', values.orderDirection, DEFAULT_ORDER_DIRECTION)
                return params
            })
        }

        return {
            setOrderBy: () => buildURL(),
            setStatus: () => buildURL(),
            setAssignee: () => buildURL(),
            setOrderDirection: () => buildURL(),
        }
    }),

    urlToAction(({ actions, values }) => {
        const urlToAction = (_: any, params: Params): void => {
            if (params.orderBy && !equal(params.orderBy, values.orderBy)) {
                if (isValidOrderBy(params.orderBy)) {
                    actions.setOrderBy(params.orderBy)
                }
            }
            if (params.status && !equal(params.status, values.status)) {
                actions.setStatus(params.status)
            }
            if (params.assignee && !equal(params.assignee, values.assignee)) {
                actions.setAssignee(params.assignee)
            }
            if (params.orderDirection && !equal(params.orderDirection, values.orderDirection)) {
                actions.setOrderDirection(params.orderDirection)
            }
        }
        return {
            '*': urlToAction,
        }
    }),

    afterMount(({ actions, values }) => {
        // The persisted orderBy is loaded straight into state (bypassing the reducer), so an
        // invalid stored value would otherwise reach the query and break the page. Reset it.
        if (!isValidOrderBy(values.orderBy)) {
            actions.setOrderBy(DEFAULT_ORDER_BY)
        }
    }),
])
